import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useGetUsers } from "../../../backend/users";
import { ACTIONS } from "../../common/Constants";
import Table, {
  CellTmpl,
  DEFAULT_OPTIONS_PAYLOAD,
  TitleTmpl,
  reactFormatter,
} from "../../common/Table";
import Icon from "../../common/Icon";
import { Button } from "react-bootstrap";
import UserLocationMapping from "./UserLocationMapping";
import { preventDefault } from "../../../utils/common";
import {
  useCreateUserLocationMapping,
  useGetUserVendorLocation,
  useGetUserCompanies,
  useCreateUserVendorLocationMapping,
} from "../../../backend/masters";
import ConfirmModal from "../../common/ConfirmModal";
import { toast } from "react-toastify";
import { API_RESULT, ERROR_MESSAGES } from "../../../utils/constants";
import MastersLayout from "../Masters/MastersLayout";
import { hasUserAccess } from "../../../backend/auth";
import { USER_PRIVILEGES } from "./Roles/RoleConfiguration";
import UserVendorLocationMapping from "./UserLocationVendorProject";

const INCLUDE_USER_ASSOCIATIONS = ['roles'];

const INCLUDE_USER_ASSOCIATION_COLUMNS = {
  roles: {
    only: ['id','name']
  }
};

function mapData(list: any[]) {
  if ((list || []).length === 0) {
    return [];
  }

  const result: any[] = [];
  list.forEach((parentCompany) => {
    const { name, associate_company, location_vendors, location, id, contact_number, user_email } =
      parentCompany;
    location_vendors.forEach((vendorLocation: any) => {
      const {
        vendor_categories: vendorCategories,
        vendor_registrations: vendorRegistrations,
      }: any = vendorLocation;
      const count = vendorRegistrations.length;
      result.push({
        id,
        name,
        location,
        associate_company,
        count,
        vendorCategoryName: vendorCategories.name,
        vendorRegistrations,
        categoryId: vendorCategories.id,
        contact_number,
        user_email
      });
    });
  });
  return result;
}

function UserVendors() {
  const [breadcrumb] = useState([
    { id: "home", label: "Home", path: "/" },
    { id: "users", label: "User Management", path: "/userManagement/roles" },
    { id: "companyMapping", label: "Company Mapping" },
  ]);
  const [user, setUser] = useState<any>(null);
  const [action, setAction] = useState(ACTIONS.NONE);
  const [data, setData] = useState<any>();
  const [params, setParams] = useState<any>();
  const [payload, setPayload] = useState<any>();
  const { records: users, isFetching: fetchingUsers } = useGetUsers({
    ...DEFAULT_OPTIONS_PAYLOAD,
    includes: INCLUDE_USER_ASSOCIATIONS,
    include: INCLUDE_USER_ASSOCIATION_COLUMNS
  });
  const { userCompanies, isFetching, refetch } = useGetUserVendorLocation(
    { filters: {
      user_id_eq: (user || {}).value
    } },
    Boolean(user)
  );
  const [userLocations, setUserLocations] = useState<any>(null);
  const { createUserVendorLocationMapping: deleteLocations } =
    useCreateUserVendorLocationMapping(
      (response: any) => {
        if (response.key === API_RESULT.SUCCESS) {
          toast.success(
            `${userLocations.associate_company_name} deleted successfully.`
          );
          setUserLocations(null);
          refetch();
        } else {
          toast.error(response.value || ERROR_MESSAGES.ERROR);
        }
      },
      () => {
        toast.error(ERROR_MESSAGES.DEFAULT);
      }
    );

  function onUserChange(e: any) {
    setUser(e);
  }

  function submitCallback() {
    setAction(ACTIONS.NONE);
    setUserLocations(null);
    refetch();
  }

  function handleCancel() {
    setUserLocations(null);
    setAction(ACTIONS.NONE);
  }

  function ActionColumnElements({ cell }: any) {
    const row = cell.getData();
    return (
      <div className="d-flex flex-row align-items-center position-relative h-100">
        {hasUserAccess(USER_PRIVILEGES.EDIT_LOCATION_MAPPING) && (
          <Icon
            className="mx-2"
            type="button"
            name={"pencil"}
            text={"Edit"}
            data={row}
            action={(event: any) => {
              setUserLocations(row);
              setAction(ACTIONS.EDIT);
            }}
          />
        )}
        {/* {hasUserAccess(USER_PRIVILEGES.DELETE_LOCATION_MAPPING) && (
          <Icon
            className="mx-2"
            type="button"
            name={"trash"}
          text={"Delete"}
            data={row}
            action={(event: any) => {
              setUserLocations(row);
              setAction(ACTIONS.DELETE);
            }}
          />
        )} */}
      </div>
    );
  }

  function CountTmpl({ cell }: any) {
    const value = cell.getValue();
    const row = cell.getData();
    return (
      <div className="d-flex h-100 align-items-center">
        <a
          href="/"
          onClick={(e: any) => {
            preventDefault(e);
            setUserLocations(row);
            setAction(ACTIONS.ADD);
          }}
        >
          {value || 0}
        </a>
      </div>
    );
  }

  const columns = [
    {
      title: "Company",
      field: "name",
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    // {
    //   title: "CompanyID",
    //   field: "id",
    //   formatter: reactFormatter(<CellTmpl />),
    //   titleFormatter: reactFormatter(<TitleTmpl />),
    // },
    {
      title: "Associate Company",
      field: "associate_company.name",
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Location",
      field: "location.name",
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      headerSort: false,
    },
    {
      title: "Contact No.",
      field: "contact_number",
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      headerSort: false,
    },
    {
      title: "Email Address",
      field: "user_email",
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      headerSort: false,
    },
    {
      title: "Vendor Category",
      field: "vendorCategoryName",
      formatter: reactFormatter(<CountTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Vendors",
      field: "count",
      formatter: reactFormatter(<CountTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Actions",
      hozAlign: "center",
      width: 160,
      headerSort: false,
      formatter: reactFormatter(<ActionColumnElements />),
      visible:
        hasUserAccess(USER_PRIVILEGES.EDIT_LOCATION_MAPPING) &&
        hasUserAccess(USER_PRIVILEGES.DELETE_LOCATION_MAPPING),
    },
  ];

  const [tableConfig] = useState({
    paginationMode: "local",
    ajaxRequestFunc,
    columns,
    rowHeight: 54,
    selectable: false,
    sortMode: "local",
  });

  function formatApiResponse(params: any, list: any, pagination = {}) {
    const result: any = mapData(list);
    const total = result.length;
    const { page, per } = params || {};
    const tdata = {
      data: result,
      total,
      last_page: Math.ceil(total / per) || 1,
      page: page || 1,
    };
    setData(tdata);
    return tdata;
  }

  function ajaxRequestFunc(url: any, config: any, params: any) {
    setParams(params);
    setPayload({ ...params });
    return Promise.resolve(formatApiResponse(params, userCompanies));
  }

  function deleteUseCompany() {
    deleteLocations({
      user_id: user.value,
      associate_company_id: userLocations.associate_company_id,
      locationVendorMappingIds: [],
    } as any);
  }

  useEffect(() => {
    if (!isFetching && payload) {
      setData(formatApiResponse(params, userCompanies));
    }
  }, [isFetching]);

  useEffect(() => {
    if (!fetchingUsers && users) {
      const _user = users[0];
      if (_user) {
        setUser({ value: _user.id, label: _user.name, user: _user });
      }
    }
  }, [fetchingUsers]);

  return (
    <>
      <MastersLayout title="Location Vendor Mapping" breadcrumbs={breadcrumb}>
        <div className="d-flex flex-column mx-0">
          <div className="card shadow d-flex flex-row justify-content-center m-3 p-3">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-end">
                <div className="col-3 px-0">
                  <Select
                    placeholder="Select User"
                    options={(users || []).map((x: any) => {
                      return { value: x.id, label: x.name, user: x };
                    })}
                    onChange={onUserChange}
                    value={user}
                    formatOptionLabel={({ label, user }) => {
                      return (
                        <div className="d-flex flex-column">
                          <span>{label}</span>
                          <span className="text-sm fst-italic fw-bold">
                            Role: {((user || {}).roles || []).map((r: any) => r.name).join(', ') || '-NA-'}
                          </span>
                        </div>
                      );
                    }}
                  />
                </div>
                {hasUserAccess(USER_PRIVILEGES.ADD_LOCATION_MAPPING) && (
                  <Button
                    variant="primary"
                    className="px-4 ms-auto text-nowrap"
                    disabled={!Boolean(user)}
                    onClick={() => setAction(ACTIONS.ADD)}
                  >
                    Add Vendor
                  </Button>
                )}
              </div>
            </div>
          </div>
          <Table data={data} options={tableConfig} isLoading={isFetching} />
        </div>
      </MastersLayout>

      {[ACTIONS.ADD, ACTIONS.EDIT, ACTIONS.VIEW].includes(action) && (
        <UserVendorLocationMapping
          user={user}
          data={userLocations}
          onClose={handleCancel}
          onSubmit={submitCallback}
        />
      )}
      {action === ACTIONS.DELETE && (
        <ConfirmModal
          title={"Delete User Location Vendor Mapping"}
          onSubmit={deleteUseCompany}
          onClose={() => setAction(ACTIONS.NONE)}
        >
          <div className="text-center mb-4">
            Are you sure you want to delete the location mapping for,{" "}
            <strong>{(userLocations || {}).associate_company_name}</strong> ?
          </div>
        </ConfirmModal>
      )}
      {/* {deletingCompany && <PageLoader message={'Deleting Company...'} />} */}
    </>
  );
}

export default UserVendors;
